import { Button, Descriptions, Icons, Select } from '@pankod/refine-antd'
import { useNotification, useTranslate } from '@pankod/refine-core'
import { definitions, Order } from 'interfaces'
import { SetStateAction, useState } from 'react'
import { SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_CANCELLED, SUBSCRIPTION_STATUS_PAUSED, supabaseClient } from 'utility'

type EditableSubscriptionStatusProps = {
  record: Order | definitions['users_subscriptions'],
  refetchFn?: Function
}

export const EditableSubscriptionStatus = ({ record, refetchFn }: EditableSubscriptionStatusProps) => {
  const t = useTranslate()
  const { open: notify } = useNotification()

  const [editfield, setEditField] = useState(false)
  const [saving, setSaving] = useState(false)
  const [currentValue, setCurrentValue] = useState<undefined | string>(
    'user_subscription_id' in record && record.user_subscription_id
      ? typeof record.user_subscription_id.status === 'string'
        ? record.user_subscription_id.status
        : undefined
      : typeof record.status === 'string'
        ? record.status
        : undefined
  )

  const handleSave = async (record: Order | definitions['users_subscriptions'], value: string) => {
    const userSubscription = ('user_subscription_id' in record && record.user_subscription_id)
      ? record.user_subscription_id
      : record

    if (!userSubscription) { return }

    if (saving) return
    setSaving(true)
    try {
      const cancelling = value === SUBSCRIPTION_STATUS_CANCELLED
      const { data, error } = await supabaseClient.rpc(
        cancelling ? 'cancel_account_subscription_v1' : 'set_account_subscription_state_v1',
        cancelling
          ? { p_subscription_row_id: userSubscription.id, p_immediate: true }
          : { p_subscription_row_id: userSubscription.id, p_state: value }
      )
      const result = data as unknown as { success?: boolean } | null
      if (error || result?.success !== true) throw new Error('Subscription change not confirmed')
      setEditField(false)
      if (refetchFn) refetchFn()
    } catch (_) {
      notify?.({ type: 'error', message: 'No se ha podido actualizar la suscripción', description: 'No se ha confirmado el cambio. Inténtalo de nuevo.' })
    } finally {
      setSaving(false)
    }
  }

  if (!currentValue) { return null }

  return <Descriptions.Item label={t('order.subscription_status')}>
      {editfield
        ? <div style={{ display: 'flex', alignItems: 'initial' }}>
          <Select
            value={t(`subscription_status.select.${currentValue}`)}
            onSelect={(value: SetStateAction<string | undefined>) => setCurrentValue(value)}
            options={[SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_PAUSED, SUBSCRIPTION_STATUS_CANCELLED].map(v => ({ label: t(`subscription_status.select.${v}`), value: v }))}
            style={{ minWidth: 150 }}
          />
          <Button disabled={saving} loading={saving} shape='circle' style={{ border: 'none', marginLeft: 10 }} icon={<Icons.SaveOutlined />} onClick={() => handleSave(record, currentValue) } />
          <Button shape='circle' style={{ border: 'none', marginLeft: 0 }} icon={<Icons.CloseOutlined />} onClick={() => { setEditField(false) }} />
        </div>
        : <div style={{ display: 'flex', alignItems: 'initial', paddingTop: '1rem' }}>
            {t(`subscription_status.select.${currentValue}`)}
            <Button shape='circle' style={{ border: 'none', marginLeft: 20, top: -5 }} icon={<Icons.EditOutlined />} onClick={() => { setEditField(true) } } />
          </div>
      }
    </Descriptions.Item>
}
